import { useState } from 'react';
import { Plug } from 'lucide-react';
import { useProjects } from '@/hooks/useProjects';
import { IntegrationListItem, type Integration } from './IntegrationCard';
import { IntegrationDetail } from './IntegrationDetail';
import { useNavigate } from 'react-router-dom';

// Category labels and order
const CATEGORY_CONFIG: Record<string, { label: string; order: number }> = {
  cli: { label: 'CLI', order: 1 },
  sdk: { label: 'SDKs', order: 2 },
  cicd: { label: 'CI/CD', order: 3 },
  hosting: { label: 'Hosting Platforms', order: 4 },
  serverless: { label: 'Serverless', order: 5 },
  kubernetes: { label: 'Kubernetes', order: 6 },
};

// Define available integrations
const INTEGRATIONS: Integration[] = [
  // CLI
  {
    id: 'cli',
    name: 'CLI',
    description: 'Command-line tool for managing secrets',
    category: 'cli',
    docsUrl: '/docs/cli',
  },
  // SDKs
  {
    id: 'node-sdk',
    name: 'Node.js SDK',
    description: 'SDK for Node.js with TypeScript support',
    category: 'sdk',
    docsUrl: '/docs/sdks/node',
  },
  {
    id: 'python-sdk',
    name: 'Python SDK',
    description: 'SDK for Python applications',
    category: 'sdk',
    docsUrl: '/docs/sdks/python',
  },
  // CI/CD Platforms
  {
    id: 'github-actions',
    name: 'GitHub Actions',
    description: 'Inject secrets into GitHub Actions workflows',
    category: 'cicd',
    docsUrl: '/docs/integrations/github-actions',
  },
  {
    id: 'bitbucket-pipelines',
    name: 'Bitbucket Pipelines',
    description: 'Inject secrets into Bitbucket Pipelines',
    category: 'cicd',
    docsUrl: '/docs/integrations/bitbucket-pipelines',
  },
  {
    id: 'gitlab-ci',
    name: 'GitLab CI',
    description: 'Load secrets in GitLab CI/CD pipelines',
    category: 'cicd',
    docsUrl: '/docs/integrations/gitlab-ci',
  },
  {
    id: 'circleci',
    name: 'CircleCI',
    description: 'Access secrets in CircleCI jobs',
    category: 'cicd',
    docsUrl: '/docs/integrations/circleci',
  },
  // Hosting Platforms
  {
    id: 'vercel',
    name: 'Vercel',
    description: 'Sync secrets to Vercel deployments',
    category: 'hosting',
    docsUrl: '/docs/integrations/vercel',
  },
  {
    id: 'netlify',
    name: 'Netlify',
    description: 'Auto-inject secrets during Netlify builds',
    category: 'hosting',
    docsUrl: '/docs/integrations/netlify',
  },
  {
    id: 'railway',
    name: 'Railway',
    description: 'Sync secrets via Railway GraphQL API',
    category: 'hosting',
    docsUrl: '/docs/integrations/railway',
  },
  // Serverless
  {
    id: 'aws-lambda',
    name: 'AWS Lambda',
    description: 'Lambda Layer for automatic secret injection',
    category: 'serverless',
    docsUrl: '/docs/integrations/aws-lambda',
  },
  // Kubernetes
  {
    id: 'kubernetes-eso',
    name: 'Kubernetes (ESO)',
    description: 'Sync secrets via External Secrets Operator',
    category: 'kubernetes',
    docsUrl: '/docs/integrations/kubernetes-eso',
  },
];

// Group integrations by category
function getGroupedIntegrations() {
  const groups = new Map<string, Integration[]>();

  for (const integration of INTEGRATIONS) {
    const existing = groups.get(integration.category) || [];
    existing.push(integration);
    groups.set(integration.category, existing);
  }

  // Sort by category order
  return Array.from(groups.entries())
    .sort(([a], [b]) => (CATEGORY_CONFIG[a]?.order ?? 99) - (CATEGORY_CONFIG[b]?.order ?? 99))
    .map(([category, integrations]) => ({
      category,
      label: CATEGORY_CONFIG[category]?.label ?? category,
      integrations,
    }));
}

export function IntegrationsPage() {
  const { environments, selectedProject } = useProjects();
  const navigate = useNavigate();
  const [selectedIntegration, setSelectedIntegration] = useState<Integration>(INTEGRATIONS[0]);
  const groupedIntegrations = getGroupedIntegrations();

  const handleCreateToken = () => {
    navigate('/tokens');
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground tracking-tight">Integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Connect KeyEnv to your favorite tools and platforms
        </p>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="flex gap-6">
        {/* Left Column - Integration List */}
        <div className="w-72 flex-shrink-0">
          <div className="space-y-4">
            {groupedIntegrations.map((group) => (
              <div key={group.category}>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 mb-1.5">
                  {group.label}
                </h3>
                <div className="space-y-0.5">
                  {group.integrations.map((integration) => (
                    <IntegrationListItem
                      key={integration.id}
                      integration={integration}
                      isSelected={selectedIntegration.id === integration.id}
                      onClick={() => setSelectedIntegration(integration)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Integration Detail */}
        <div className="flex-1 min-w-0">
          {selectedProject ? (
            <IntegrationDetail
              integration={selectedIntegration}
              environments={environments}
              projectId={selectedProject.id}
              onCreateToken={handleCreateToken}
            />
          ) : (
            <div className="py-20 flex items-center justify-center">
              <div className="text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Plug className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">Select a Project</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Choose a project from the sidebar to see integration configurations with your project ID pre-filled.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
